import axios from 'axios';
import qs from 'qs';
import errorHandling from './errorHandling.js';

export class XaAjax {
	constructor(baseUrl = '', passBackErrors = false) {
		axios.defaults.headers.common.Pragma = 'no-cache'; // prevents IE from using cached responses
		this.baseURL = baseUrl;
		this.activeCancels = {};
		this.passBackErrors = passBackErrors;
		setupFileErrorParsingInterceptor();
	}

	// Any request made with a key will cancel any pending requests made with that same key.

	get(path, params, options, key) {
		const config = buildConfig('get', this.baseURL, path, params, options);
		return this.request(config, key);
	}

	post(path, data, options, key) {
		const config = buildConfig('post', this.baseURL, path, data, options);
		return this.request(config, key);
	}

	put(path, data, options, key) {
		const config = buildConfig('put', this.baseURL, path, data, options);
		return this.request(config, key);
	}

	delete(path, data, options, key) {
		const config = buildConfig('delete', this.baseURL, path, data, options);
		return this.request(config, key);
	}

	request(config, key) {
		if (validKey(key)) {
			if (this.activeCancels.hasOwnProperty(key)) {
				const cancel = this.activeCancels[key];
				cancel();
			}

			config.cancelToken = new axios.CancelToken((cancel) => {
				this.activeCancels[key] = cancel;
			});
		}

		const promise = new Promise((resolve, reject) => {
			axios(config)
				.then((response) => {
					if (validKey(key) && this.activeCancels.hasOwnProperty(key)) {
						delete this.activeCancels[key];
					}

					if (response.status >= 200 && response.status < 300) {
						resolve(response.data);
					}
					else if (this.passBackErrors) {
						reject(response);
					}
					else {
						logError(config);
						reject(errorHandling.getErrorMessage(response));
					}
				})
				.catch((error) => {
					if (this.passBackErrors) {
						logError(config);
						reject(error);
					}
					else if (axios.isCancel(error)) {
						console.log(`Request "${ key }" was cancelled.`);
					}
					else if (error.response && error.response.status && error.response.status === 401) {
						if (!window.xaUserNotifiedSessionExpired) {
							window.xaUserNotifiedSessionExpired = true;
							handleExpiredSession();
						}
					}
					else {
						logError(config);
						reject(errorHandling.getErrorMessage(error));
					}
				});
		});

		return promise;
	}
}

export function handleExpiredSession() {
	if (window.jTimeout) {
		window.jTimeout.setExpiration(0);
	}
	else {
		const alertMsg = `${ window.getTranslation('Your session has expired.') } ${ window.getTranslation('Please log in again.') }`;
		alert(alertMsg);
		window.location.reload();
	}
}

function setupFileErrorParsingInterceptor() {
	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (
				error.request
				&& error.request.responseType
				&& error.request.responseType === 'blob'
				&& error.response.data instanceof Blob
				&& error.response.data.type
				&& error.response.data.type.toLowerCase().indexOf('json') !== -1
			) {
				return new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => {
						error.response.data = JSON.parse(reader.result);
						resolve(Promise.reject(error));
					};

					reader.onerror = () => {
						reject(error);
					};

					reader.readAsText(error.response.data);
				});
			}

			return Promise.reject(error);
		}
	);
}

function logError(config) {
	console.warn(`An error was caught during the request:\n${ JSON.stringify(config) }`);
}

function validKey(key) {
	let result = false;
	if (key && typeof key === 'string') {
		result = true;
	}
	return result;
}

function buildConfig(method, baseURL, path, data, options) {
	let config = {
		paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'comma' })
	};

	if (options && typeof options === 'object') {
		config = options;
	}

	config.method = method;
	config.url = baseURL + path;

	if (data) {
		if (method === 'get') {
			config.params = data;
		}
		else {
			config.data = data;
		}
	}

	return config;
}